import React from 'react';

/**
 * Kanonischer Button des Design-Systems.
 *
 * Regeln (siehe frontend/docs/design-system/TOKENS.md):
 * - Nur Token-Farben (bg-primary etc.), KEINE Gradients, KEINE Hex-Literale
 * - Ein Radius für alle Buttons: rounded-xl
 * - Touch-Target min. 40/48/56px je nach Größe
 *
 * Für <Link>/<a> die Klassen via buttonClasses(variant, size) übernehmen.
 */

// `active:` = der Knopf gibt unter dem Finger nach (Aug 2026).
//
// Vorher passierte beim Druecken NICHTS bis zum Loslassen — der einzige
// Zustandswechsel war `hover`, und den gibt es auf dem Telefon gar nicht. Auf
// dem iPhone fuehlte sich jeder Knopf deshalb an wie ein Bild von einem Knopf.
// Zwei Prozent Schrumpfen und ein Pixel nach unten sind genau der Betrag, den
// man spuert, ohne ihn zu sehen; `duration-fast` (150ms) statt der bisherigen
// 300ms, weil eine Reaktion auf Beruehrung sofort kommen muss — alles darueber
// liest sich als Verzoegerung. `ease-spring` gilt fuer beide Richtungen: Der
// Knopf federt beim Loslassen mit einem Hauch Ueberschwinger zurueck, wie eine
// echte Taste.
//
// Oktober 2026, zwei Dinge:
// - `transition-all` ist einer Liste der Eigenschaften gewichen, die sich
//   wirklich ändern. `all` animierte auch Breite, Polster und Schrift mit,
//   sobald eine Klasse wechselte, und kostete beim Scrollen GPU-Zeit.
// - Deaktiviert sieht überall gleich aus: 40 % Deckkraft (Material 3 nennt
//   38 %), kein Schatten, Sperr-Cursor. Vorher hatte jede Variante ihre eigene
//   Umschaltung auf Grau, und in der App standen fünf Deckkraftwerte
//   nebeneinander. WANN ein Knopf deaktiviert ist, entscheidet weiter der
//   Aufrufer — Aufrufer setzen dafür keine eigenen `disabled:`-Klassen mehr.
const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-xl ' +
  'transition-[transform,background-color,border-color,color,box-shadow,opacity] duration-fast ease-spring touch-manipulation ' +
  'active:scale-[0.98] active:translate-y-px ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
  'disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed disabled:active:scale-100 disabled:active:translate-y-0';

// Gefüllte Varianten tragen bewusst KEINEN farbigen Schein mehr (vorher
// `shadow-lg shadow-<farbe>/20`): ein eingefärbter Halo unter einer ohnehin
// gesättigten Vollfläche liest sich als Leuchtreklame statt als Knopf. Ein
// neutraler Token-Schatten hebt den Button genauso vom Papier ab.
// Die Fläche selbst kommt aus den cta-Tokens, nicht aus primary — siehe index.css.
//
// `secondary` ist seit Oktober 2026 deckend (vorher `bg-surface/70` mit
// `backdrop-blur-sm`): Glas gibt es nur noch in der schwebenden Navigation
// (CorporateDesign §5), und ein Weichzeichner je Knopf kostete beim Scrollen
// spürbar Bildrate.
const VARIANTS = {
  primary: 'bg-cta text-cta-foreground hover:bg-cta-hover shadow-token-sm hover:shadow-token-md',
  secondary: 'bg-surface border border-border text-foreground hover:bg-surface-raised shadow-token-sm',
  ghost: 'text-foreground hover:bg-foreground/5',
  danger: 'bg-danger text-danger-foreground hover:opacity-90 shadow-token-sm',
  success: 'bg-success text-success-foreground hover:opacity-90 shadow-token-sm',
};

// Größen sind mobile-first: auf dem Telefon kompakt (44px = iOS-HIG-Minimum),
// ab sm: die gewohnten Desktop-Maße. Vorher galt die Desktop-Größe überall — das
// waren die "viel zu großen Buttons" auf dem iPhone (Phase 4 — Layout & Dichte).
const SIZES = {
  sm: 'min-h-[40px] px-3 text-xs sm:text-sm',
  md: 'min-h-[44px] px-4 text-sm sm:min-h-[48px] sm:px-5 sm:text-base',
  lg: 'min-h-[48px] px-6 text-sm sm:min-h-[56px] sm:px-8 sm:text-base',
};

// `fullWidth` macht volle Breite zur ausdrücklichen Entscheidung.
// Bewusst KEIN Default-Breitenwert: In einem `flex-col`-Elternteil streckt
// align-items:stretch den Button genau dann, wenn seine Breite `auto` ist — ein
// pauschales `w-auto` würde das also nicht verhindern, aber bestehende, absichtlich
// breite Buttons gefährden. Wer das Strecken unterbinden will, setzt am Aufrufort
// `sm:w-auto` (Zeile wird zu flex-row) bzw. `self-start`.
/**
 * Quadratischer Icon-Knopf (Tabellenzeilen, Blaetter-Pfeile, Werkzeugleisten).
 *
 * Stand bis August 2026 nur als `SharedStyles.buttonIcon` da — also ausgerechnet
 * die eine Knopf-Rolle, die es NICHT ins Design-System geschafft hatte, im
 * abgeloesten Stylesheet. Die Klassen sind unveraendert uebernommen; die
 * Konstante in SharedStyles zeigt jetzt hierher (eine Wahrheit statt zwei).
 */
export const iconButtonClasses = (className = '') =>
  [
    'min-h-[48px] min-w-[48px] p-2 bg-surface-raised text-foreground/80 rounded-xl border border-border',
    'transition-colors duration-base hover:bg-surface hover:text-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'disabled:cursor-not-allowed disabled:opacity-40',
    className,
  ]
    .filter(Boolean)
    .join(' ');

/**
 * Icon-Knopf in Tabellenzeilen (Paket 12b, Sep 2026): 36px-Ziel — mehr passt
 * nicht in eine Zeile, dafuer stehen die Knoepfe weit genug auseinander.
 * Fokusring immer, Farbe nur wo sie Bedeutung hat: `danger` fuer Loeschen/
 * Ablehnen, `success` fuer Genehmigen, `primary` fuer die eine Hauptaktion
 * der Zeile; alles andere neutral. Vorher hatten Tabellen bis zu vier
 * Icon-Farben nebeneinander (blau/gruen/rot/lila) ohne zugaenglichen Namen.
 */
export const rowIconButtonClasses = (tone = 'neutral', className = '') => {
  const tones = {
    neutral: 'text-muted-foreground hover:text-foreground hover:bg-foreground/5',
    primary: 'text-primary hover:bg-primary-soft',
    success: 'text-success hover:bg-success-soft',
    danger: 'text-danger hover:bg-danger-soft',
  };
  return [
    // 44px am Telefon (Zeilenaktionen sind oft das einzige Ziel und liegen
    // mit gap-1 nebeneinander), 36px ab Tablet wie bisher.
    'inline-flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-lg transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'disabled:cursor-not-allowed disabled:opacity-40',
    tones[tone] || tones.neutral,
    className,
  ].filter(Boolean).join(' ');
};

export const buttonClasses = (variant = 'primary', size = 'md', className = '', fullWidth = false) =>
  [
    BASE,
    VARIANTS[variant] || VARIANTS.primary,
    SIZES[size] || SIZES.md,
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

const Button = React.forwardRef(
  ({ variant = 'primary', size = 'md', type = 'button', fullWidth = false, className = '', children, ...rest }, ref) => (
    <button ref={ref} type={type} className={buttonClasses(variant, size, className, fullWidth)} {...rest}>
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export default Button;
